import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { createPromoCoupon, getPromoCouponById, updatePromoCoupon } from '../../../services/timesPoint/promoCoupon';

const PromoCouponForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'web',
    state: 'ALL_USERS',
    viewport: '',
    height: '',
    width: '',
    status: false,
    banners: [{
      linkUrl: '',
      sequenceNo: '',
      title: '',
      imageUrl: '',
      startDate: Date.now(),
      endDate: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days from now
      pageType: ['all']
    }]
  });

  useEffect(() => {
    if (id !== '0') {
      fetchPromoCoupon();
    }
  }, [id]);

  const fetchPromoCoupon = async () => {
    try {
      setLoading(true);
      const response = await getPromoCouponById(id);
      if (response.data && response.data.status === 'SUCCESS') {
        setFormData(response.data.data);
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Ok'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' ? checked : value
    }));
  };

  const handleBannerChange = (index, field, value) => {
    const newBanners = [...formData.banners];
    newBanners[index] = {
      ...newBanners[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      banners: newBanners
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.height || !formData.width) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Height and Width are required fields',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    // Validate banner fields
    const invalidBanners = formData.banners.filter(banner => 
       !banner.sequenceNo || !banner.linkUrl || !banner.imageUrl || !banner.title
    );

    if (invalidBanners.length > 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'All banner fields (Height, Width, Sequence, Link URL, Image URL, Title) are required',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
      return;
    }

    try {
      setLoading(true);
      const response = id === '0' 
        ? await createPromoCoupon(formData)
        : await updatePromoCoupon(id, formData);

      if (response.data && response.data.status === 'SUCCESS') {
        Swal.fire({
          title: 'Success',
          text: `Banners ${id === '0' ? 'created' : 'updated'} successfully`,
          icon: 'success',
          confirmButtonText: 'Ok'
        }).then(() => {
          navigate('/app/banners');
        });
      } else {
        throw new Error(response?.message || 'Operation failed');
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.message || 'Something went wrong',
        icon: 'error',
        confirmButtonText: 'Ok'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {id === '0' ? 'Create' : 'Edit'} Banners
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                label="Type"
              >
                <MenuItem value="web">Web</MenuItem>
                <MenuItem value="msite">Mobile</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                name="state"
                value={formData.state}
                onChange={handleChange}
                label="State"
              >
                <MenuItem value="ALL_USERS">All Users</MenuItem>
                <MenuItem value="loggedIn">LoggedIn Users</MenuItem>
                <MenuItem value="prelogin">Logour Users</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Viewport"
              name="viewport"
              value={formData.viewport}
              onChange={handleChange}
              error={!formData.viewport}
              helperText={!formData.viewport ? 'Viewport is required' : ''}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={handleChange}
                  name="status"
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              type="number"
              label="Height"
              name="height"
              value={formData.height}
              onChange={handleChange}
              error={!formData.height}
              helperText={!formData.height ? 'Height is required' : ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              type="number"
              label="Width"
              name="width"
              value={formData.width}
              onChange={handleChange}
              error={!formData.width}
              helperText={!formData.width ? 'Width is required' : ''}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Banner Details
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    banners: [...prev.banners, {
                      linkUrl: '',
                      sequenceNo: prev.banners.length + 1,
                      title: '',
                      imageUrl: '',
                      startDate: Date.now(),
                      endDate: Date.now() + (30 * 24 * 60 * 60 * 1000),
                      pageType: ['all']
                    }]
                  }));
                }}
              >
                Add Banner
              </Button>
            </Box>
            {formData.banners.map((banner, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Banner {index + 1}</Typography>
                  {formData.banners.length > 1 && (
                    <Button
                      color="error"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          banners: prev.banners.filter((_, i) => i !== index)
                        }));
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Link URL"
                      value={banner.linkUrl}
                      onChange={(e) => handleBannerChange(index, 'linkUrl', e.target.value)}
                      error={!banner.linkUrl}
                      helperText={!banner.linkUrl ? 'Link URL is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Image URL"
                      value={banner.imageUrl}
                      onChange={(e) => handleBannerChange(index, 'imageUrl', e.target.value)}
                      error={!banner.imageUrl}
                      helperText={!banner.imageUrl ? 'Image URL is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Title"
                      value={banner.title}
                      onChange={(e) => handleBannerChange(index, 'title', e.target.value)}
                      error={!banner.title}
                      helperText={!banner.title ? 'Title is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label="Sequence Number"
                      value={banner.sequenceNo}
                      onChange={(e) => handleBannerChange(index, 'sequenceNo', parseInt(e.target.value))}
                      error={!banner.sequenceNo}
                      helperText={!banner.sequenceNo ? 'Sequence Number is required' : ''}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Start Date"
                      value={
                        banner.startDate && !isNaN(new Date(Number(banner.startDate)))
                          ? new Date(Number(banner.startDate)).toISOString().slice(0, 16)
                          : ''
                      }
                      onChange={(e) => handleBannerChange(index, 'startDate', new Date(e.target.value).getTime())}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="End Date"
                      value={
                        banner.endDate && !isNaN(new Date(Number(banner.endDate)))
                          ? new Date(Number(banner.endDate)).toISOString().slice(0, 16)
                          : ''
                      }
                      onChange={(e) => handleBannerChange(index, 'endDate', new Date(e.target.value).getTime())}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Page Type</InputLabel>
                      <Select
                        multiple
                        value={banner.pageType}
                        onChange={(e) => handleBannerChange(index, 'pageType', e.target.value)}
                        label="Page Type"
                      >
                        <MenuItem value="all">All Pages</MenuItem>
                        <MenuItem value="remittance">Remittance</MenuItem>
                        <MenuItem value="earn">Earn</MenuItem>
                        <MenuItem value="redeem">Redeem</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/app/banners')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default PromoCouponForm; 